import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import { TickCircleIcon } from '@nebula/icons/ui';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import GlobalStyles from '@nebula/global-styles';
import { useHistory } from 'react-router-dom';
import { ChangeBillingDateStage, changeBillingDateTransition } from '@ecs-uk/web-common-mobile-view-helpers';
import { PaymentMethodTypes } from '../../../components/PaymentCaptureMethod/constants.ts';
import {
  SuccessPageContainer,
  ConfirmationDetails,
  ConfirmationStatus,
  MainContent,
  SuccessIcon,
  Heading,
  SubHeading,
  DetailsBox,
  DetailRow,
  DetailLabel,
  DetailValue,
  ActionButton,
} from './index.styles';
import { publishOneTimePaymentSuccess } from '../../../helpers/changeBillingDate/analytics';
import useExistingPaymentMethodDisplay from '../../../components/PaymentCaptureMethod/hooks/useExistingPaymentMethodDisplay';
import { getBilling } from '../../../actions/billing';
import { getCurrencyAriaLabel } from '../../../helpers';
import { PaymentContext } from '../../../components/PaymentOrchestrator/PaymentContextProvider';
import usePageView from '../../../hooks/usePageView.js';

const PAGE_NAME = 'ProdMoveChangeBillingDateSuccessPage';

const ChangeBillingDateSuccess = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const headingRef = useRef(null);

  const { isWebview } = useContext(PaymentContext);

  const { nextBillingDate, adjustmentDays, newBillDate, billingPrice, amountPaid } = useSelector(state => ({
    nextBillingDate: state.billing?.nextBillingDate,
    adjustmentDays: state.billingDateSelection?.adjustmentDays,
    newBillDate: state.changeBillingDate?.newBillDate,
    billingPrice: state.billing?.signupPrice,
    amountPaid: state.changeBillingDate?.price,
  }));

  usePageView(PAGE_NAME);

  const { existingPaymentType, paymentMethodDisplayText, paymentAriaLabel } = useExistingPaymentMethodDisplay();

  const oldBillDate = dayjs(nextBillingDate).format('MM/DD/YYYY');
  const billingDay = `${dayjs(newBillDate).format('Do')} of every month`;
  const nextBillDateLong = dayjs(newBillDate).format('D MMMM YYYY');
  const nextBillDue = `£${billingPrice} on ${nextBillDateLong}`;

  const paymentDetails = useMemo(
    () => [
      {
        label: 'New billing date:',
        value: billingDay ? `${billingDay}` : '',
        ariaLabel: `New billing date ${billingDay}`,
      },
      {
        label: 'Next bill due:',
        value: nextBillDue,
        ariaLabel: `Next bill due ${getCurrencyAriaLabel(billingPrice)} on ${nextBillDateLong}`,
      },
      {
        label: 'Amount paid:',
        value: amountPaid ? `£${amountPaid.toFixed(2)}` : '',
        ariaLabel: `Amount paid ${getCurrencyAriaLabel(amountPaid)}`,
      },
      {
        label: 'Payment method:',
        value: paymentMethodDisplayText,
        ariaLabel: `Payment method ${paymentAriaLabel}`,
      },
    ],
    [billingDay, nextBillDue, billingPrice, nextBillDateLong, amountPaid, paymentMethodDisplayText, paymentAriaLabel]
  );

  useEffect(() => {
    publishOneTimePaymentSuccess({
      newBillDate: dayjs(newBillDate).format('MM/DD/YYYY'),
      oldBillDate,
      amountPaid,
      adjustmentDays,
      paymentMethod: existingPaymentType === PaymentMethodTypes.Card ? 'existing_card' : existingPaymentType,
    });

    // Refresh billing info to reflect changes
    dispatch(getBilling());
  }, []);

  useEffect(() => {
    // Focus heading for screen readers to announce success
    const timer = setTimeout(() => {
      headingRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isWebview) {
      changeBillingDateTransition({ stage: ChangeBillingDateStage.SUCCESS });
    }
  }, [isWebview]);

  return (
    <ThemeProvider theme={nebulaTheme}>
      <GlobalStyles />
      <SuccessPageContainer isWebview={isWebview}>
        <MainContent>
          <ConfirmationStatus as="output">
            <SuccessIcon aria-hidden="true">
              <TickCircleIcon iconSize="large" iconColor={nebulaTheme.color.ui.grey[100]} />
            </SuccessIcon>
            <Heading id="success-heading" tabIndex={-1} ref={headingRef}>
              Success
            </Heading>
            <SubHeading>Your billing details have been updated.</SubHeading>
          </ConfirmationStatus>
          <ConfirmationDetails>
            <DetailsBox as="section">
              {paymentDetails.map(item => (
                <DetailRow key={item.label} aria-label={item.ariaLabel}>
                  <DetailLabel aria-hidden="true">{item.label}</DetailLabel>
                  <DetailValue aria-hidden="true">{item.value}</DetailValue>
                </DetailRow>
              ))}
            </DetailsBox>

            <ActionButton
              id="back_to_payment_and_billing"
              label="Back to Payment and billing"
              buttonKind="primaryMagenta"
              buttonSize="large"
              type="button"
              onClick={() => {
                const paymentAndBillingPath = `${isWebview ? '/mobile' : ''}/billing`;
                history.replace(paymentAndBillingPath);
              }}
            />
          </ConfirmationDetails>
        </MainContent>
      </SuccessPageContainer>
    </ThemeProvider>
  );
};

export default ChangeBillingDateSuccess;
